import CheckboxField from "components/controls/CheckboxField";
import ComboBoxField from "components/controls/ComboBoxField";
import DatePickerField from "components/controls/DatePickerField";
import DoubleNumberInputField from "components/controls/DoubleNumberInputField";
import NumberInputField from "components/controls/NumberInputField";
import TextInputField from "components/controls/TextInputField";
import { vehicleFormClasses } from "pages/vehicle/VehicleForm";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "store/hooks";
import {
  isDisabledExternal,
  isDisabledExternalOrHasBeenActivated,
} from "../../../helpers";
import useInsurerList from "./hooks/useInsurerList";

const FinanceTempMachineryBreakdownFields = ({
  formikProps,
  userRole,
  route,
  vehicle,
  insurerList,
}) => {
  const { t } = useTranslation();
  const { setFieldValue, handleChange, values, errors } = formikProps;
  const taxonomy = useSelector((s) => s.taxonomy);
  const vehicleAppSettings = useSelector((s) => s.vehicle.vehicleAppSettings);

  const paymentMethodOptions = useMemo(() => {
    return taxonomy?.PaymentMethod?.items
      .filter((item) => vehicleAppSettings?.PAYMENT_METHODS.includes(item.code))
      .map((item) => ({
        value: item.id,
        label: t(item.code),
      }));
  }, [t, taxonomy?.PaymentMethod?.items, vehicleAppSettings]);

  const policyStatusOptions = useMemo(() => {
    return taxonomy?.VehiclePolicyStatusCode?.items.map((item) => ({
      value: item.id,
      label: t(item.code),
    }));
  }, [t, taxonomy?.VehiclePolicyStatusCode?.items]);

  const paymentMeanOptions = useMemo(() => {
    return taxonomy?.PremiumPaymentMean?.items.map((item) => ({
      value: item.id,
      label: t(item.code),
    }));
  }, [t, taxonomy?.PremiumPaymentMean?.items]);

  const insurerData = useInsurerList({
    insurerList,
    values,
    name: "machineryBreakdownInsurerPartnerNumber",
    subName: "machineryBreakdownInsurerBranchOffice",
    setFieldValue,
  });

  return (
    <>
      <div className={vehicleFormClasses.item}>
        <CheckboxField
          checked={values?.machineryBreakdownIsClosingDate}
          errors={errors}
          setFieldValue={setFieldValue}
          name={"machineryBreakdownIsClosingDate"}
          disabled={false}
          label={"bfm.machineryBreakdownIsClosingDate.label"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "machineryBreakdownIsClosingDate"
          )}
        />
      </div>

      <div className={vehicleFormClasses.item}>
        <CheckboxField
          checked={values?.isFirstForMachineryBreakdownClosingDate}
          errors={errors}
          setFieldValue={setFieldValue}
          name={"isFirstForMachineryBreakdownClosingDate"}
          disabled={false}
          label={"bfm.isFirstForMachineryBreakdownClosingDate.label"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "isFirstForMachineryBreakdownClosingDate"
          )}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <DatePickerField
          errors={errors}
          setFieldValue={setFieldValue}
          defaultValue={values.machineryBreakdownLastStartDate}
          name={"machineryBreakdownLastStartDate"}
          disabled={route === "readOnlyVehicle"}
          label={"bfm.vehicleForm.startDate.label"}
          placeholder={t("bfm.selectDate")}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "machineryBreakdownLastStartDate"
          )}
          confidentiality={null}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <DatePickerField
          errors={errors}
          setFieldValue={setFieldValue}
          defaultValue={values.machineryBreakdownImportEndDate}
          name={"machineryBreakdownImportEndDate"}
          disabled={route === "readOnlyVehicle"}
          label={"bfm.vehicleForm.endDate.label"}
          placeholder={t("bfm.selectDate")}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "machineryBreakdownImportEndDate"
          )}
          confidentiality={null}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <DatePickerField
          errors={errors}
          setFieldValue={setFieldValue}
          defaultValue={values.machineryBreakdownPolicyCancelationDocumentDate}
          name={"machineryBreakdownPolicyCancelationDocumentDate"}
          disabled={route === "readOnlyVehicle"}
          label={"bfm.vehicleForm.cancelationDocumentDate.label"}
          placeholder={t("bfm.selectDate")}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "machineryBreakdownPolicyCancelationDocumentDate"
          )}
          confidentiality={null}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <ComboBoxField
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            route,
            vehicle,
            userRole,
          })}
          errors={errors}
          name="machineryBreakdownInsurerPartnerNumber"
          label={"bfm.fleetForm.machineryBreakdownInsurerPartnerNumber.label"}
          setFieldValue={setFieldValue}
          options={insurerData.insurerOptions}
          defaultValue={values?.machineryBreakdownInsurerPartnerNumber}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "machineryBreakdownInsurerPartnerNumber"
          )}
          confidentiality={null}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <ComboBoxField
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            route,
            vehicle,
            userRole,
          })}
          errors={errors}
          name="machineryBreakdownInsurerBranchOffice"
          label={"bfm.vehicleForm.insurerBranchOffice.label"}
          setFieldValue={setFieldValue}
          options={insurerData.insurerBranchOfficeOptions}
          defaultValue={values?.machineryBreakdownInsurerBranchOffice}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "machineryBreakdownInsurerBranchOffice"
          )}
          confidentiality={null}
        />
      </div>

      <div className={vehicleFormClasses.item}>
        <TextInputField
          value={values.machineryBreakdownPolicyNumber}
          onChange={handleChange}
          errors={errors}
          name={"machineryBreakdownPolicyNumber"}
          // multiline
          // rows={4}
          disabled={isDisabledExternal({ vehicleAppSettings, userRole, route })}
          label={"bfm.vehicleForm.policyNumber.label"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "machineryBreakdownPolicyNumber"
          )}
        />
      </div>
      {vehicleAppSettings.DEFAULT_COLUMNS.includes(
        "machineryBreakdownPolicyStatus"
      ) ? (
        <div className={vehicleFormClasses.item}>
          <ComboBoxField
            disabled={isDisabledExternal({
              vehicleAppSettings,
              userRole,
              route,
            })}
            errors={errors}
            name="machineryBreakdownPolicyStatus"
            label={"bfm.vehicleForm.machineryBreakdownPolicyStatus.label"}
            setFieldValue={setFieldValue}
            options={policyStatusOptions}
            defaultValue={values.machineryBreakdownPolicyStatus}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "machineryBreakdownPolicyStatus"
            )}
            confidentiality={null}
          />
        </div>
      ) : null}
      <div className={vehicleFormClasses.item}>
        <ComboBoxField
          disabled={isDisabledExternal({ vehicleAppSettings, userRole, route })}
          errors={errors}
          name="machineryBreakdownPaymentMethod"
          label={"bfm.vehicleForm.paymentMethod.label"}
          setFieldValue={setFieldValue}
          options={paymentMethodOptions}
          defaultValue={values.machineryBreakdownPaymentMethod}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "machineryBreakdownPaymentMethod"
          )}
          confidentiality={null}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <ComboBoxField
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            route,
            vehicle,
            userRole,
          })}
          errors={errors}
          name="machineryBreakdownPaymentMean"
          label={"bfm.vehicleForm.paymentMean.label"}
          setFieldValue={setFieldValue}
          options={paymentMeanOptions}
          defaultValue={values.machineryBreakdownPaymentMean}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "machineryBreakdownPaymentMean"
          )}
          confidentiality={null}
        />
      </div>

      <div className={vehicleFormClasses.item}>
        <DoubleNumberInputField
          setFieldValue={setFieldValue}
          value={values?.machineryBreakdownAnnualGrossPremium}
          onChange={handleChange}
          errors={errors}
          name={"machineryBreakdownAnnualGrossPremium"}
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            route,
            vehicle,
            userRole,
          })}
          label={"bfm.vehicleForm.annualGrossPremium.label"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "machineryBreakdownAnnualGrossPremium"
          )}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <DoubleNumberInputField
          setFieldValue={setFieldValue}
          value={values?.machineryBreakdownAnnualNetPremium}
          onChange={handleChange}
          errors={errors}
          name={"machineryBreakdownAnnualNetPremium"}
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            route,
            vehicle,
            userRole,
          })}
          label={"bfm.vehicleForm.annualNetPremium.label"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "machineryBreakdownAnnualNetPremium"
          )}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <NumberInputField
          setFieldValue={setFieldValue}
          value={values.machineryBreakdownSuspensionDays}
          onChange={handleChange}
          errors={errors}
          name={"machineryBreakdownSuspensionDays"}
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            route,
            vehicle,
            userRole,
          })}
          label={"bfm.suspensionDays"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "machineryBreakdownSuspensionDays"
          )}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <NumberInputField
          setFieldValue={setFieldValue}
          value={values.machineryBreakdownActivationDays}
          onChange={handleChange}
          errors={errors}
          name={"machineryBreakdownActivationDays"}
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            route,
            vehicle,
            userRole,
          })}
          label={"bfm.activationDays"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "machineryBreakdownActivationDays"
          )}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <NumberInputField
          setFieldValue={setFieldValue}
          value={values.machineryBreakdownSuspensionDaysLimit}
          onChange={handleChange}
          errors={errors}
          name={"machineryBreakdownSuspensionDaysLimit"}
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            userRole,
            route,
            vehicle,
          })}
          label={"bfm.suspensionDaysLimit"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "machineryBreakdownSuspensionDaysLimit"
          )}
          isNegativeAllowed={true}
        />
      </div>

      <div className={vehicleFormClasses.item}>
        <NumberInputField
          setFieldValue={setFieldValue}
          value={values.machineryBreakdownDueMonth}
          onChange={handleChange}
          errors={errors}
          inputProps={{ type: "number", max: "12" }}
          name={"machineryBreakdownDueMonth"}
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            route,
            vehicle,
            userRole,
          })}
          label={"bfm.vehicleForm.dueMonth.label"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "machineryBreakdownDueMonth"
          )}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <NumberInputField
          setFieldValue={setFieldValue}
          value={values.machineryBreakdownDueDay}
          onChange={handleChange}
          errors={errors}
          name={"machineryBreakdownDueDay"}
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            route,
            vehicle,
            userRole,
          })}
          label={"bfm.vehicleForm.dueDay.label"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "machineryBreakdownDueDay"
          )}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <DatePickerField
          errors={errors}
          setFieldValue={setFieldValue}
          defaultValue={values.machineryBreakdownPolicyDocumentDate}
          name={"machineryBreakdownPolicyDocumentDate"}
          disabled={route === "readOnlyVehicle"}
          label={"bfm.vehicleForm.documentDate.label"}
          placeholder={t("bfm.selectDate")}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "machineryBreakdownPolicyDocumentDate"
          )}
          confidentiality={null}
        />
      </div>
      <div className={vehicleFormClasses.fullWidthFormItem}>
        <CheckboxField
          checked={values?.machineryBreakdownIsPolicyIssued}
          errors={errors}
          setFieldValue={setFieldValue}
          name={"machineryBreakdownIsPolicyIssued"}
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            userRole,
            route,
            vehicle,
          })}
          label={"bfm.isPolicyIssued"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "machineryBreakdownIsPolicyIssued"
          )}
        />
      </div>
      <div className={vehicleFormClasses.fullWidthFormItem}>
        <TextInputField
          value={values.machineryBreakdownComment}
          onChange={handleChange}
          errors={errors}
          name={"machineryBreakdownComment"}
          multiline
          rows={4}
          disabled={isDisabledExternal({ vehicleAppSettings, userRole, route })}
          label={"bfm.fleetForm.fleetComment.label"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "machineryBreakdownComment"
          )}
        />
      </div>
    </>
  );
};

export default FinanceTempMachineryBreakdownFields;
